import { z } from "zod";
import { dateOnlySchema, idParamSchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, csvArraySchema, queryBooleanSchema, timestampsShape } from "./common.ts";
import { GST_INVOICE_NUMBER_REGEX, gstStateCodeSchema } from "./india.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { UNBILLED_GROUPINGS } from "./time.ts";

/**
 * GST DOCUMENTS — tax invoices, proformas, credit/debit notes, receipt vouchers
 * and bills of supply (PRD §5.2, §5.3 and §7 Item 4; principles §7.4).
 *
 * Every document type lives in ONE table and shares one lifecycle, because they
 * share the parts that are legally load-bearing: a gapless serial number per
 * GSTIN per financial year, the buyer/supplier identity captured at issue, the
 * Rule 46 field set, and per-line + SAC-wise tax that reconciles to the header
 * to the paisa.
 *
 * Money is `*Minor` integers. Every money field is NULLABLE in a response DTO
 * because a caller without `financials:view` gets `null` instead of the figure
 * (the API masks at its edge, never in the data layer).
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** GST document kinds; each carries its own gapless series (principles §7.4). */
export const INVOICE_DOC_TYPES = ["INV", "CRN", "DBN", "RV", "RFV", "PI", "BOS"] as const;
export type InvoiceDocType = (typeof INVOICE_DOC_TYPES)[number];
export const invoiceDocTypeSchema = z.enum(INVOICE_DOC_TYPES);

export const INVOICE_DOC_TYPE_LABELS: Record<InvoiceDocType, string> = {
  INV: "Tax Invoice",
  CRN: "Credit Note",
  DBN: "Debit Note",
  RV: "Receipt Voucher",
  RFV: "Refund Voucher",
  PI: "Proforma Invoice",
  BOS: "Bill of Supply",
};

/** Fallback series prefixes (INV and CRN come from the org's invoice settings). */
export const INVOICE_DEFAULT_PREFIXES: Record<InvoiceDocType, string> = {
  INV: "INV/",
  CRN: "CN/",
  DBN: "DN/",
  RV: "RV/",
  RFV: "RFV/",
  PI: "PI/",
  BOS: "BOS/",
};

/** Documents that create a receivable the client must settle. */
export const RECEIVABLE_DOC_TYPES = ["INV", "DBN", "BOS"] as const;

/** PRD US-4.4. `overdue` is derived nightly from `dueDate` + balance, never set by hand. */
export const INVOICE_STATUSES = ["draft", "issued", "sent", "viewed", "partially_paid", "paid", "overdue", "cancelled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  issued: "Issued",
  sent: "Sent",
  viewed: "Viewed",
  partially_paid: "Partially paid",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

/** A document that has left draft carries a number and can never be edited again. */
export function isIssuedStatus(status: InvoiceStatus): boolean {
  return status !== "draft";
}

/** Place-of-supply outcome (mirrors `gstSupplyType` in india.ts). */
export const GST_SUPPLY_TYPES = ["intra_state", "inter_state", "export"] as const;
export const gstSupplyTypeSchema = z.enum(GST_SUPPLY_TYPES);

/** Rule 46(o) endorsement printed on a zero-rated export invoice raised under LUT. */
export const LUT_ENDORSEMENT = "Supply meant for export under letter of undertaking without payment of integrated tax";
/** Endorsement when IGST is paid on the export instead (PRD item 17). */
export const EXPORT_WITH_TAX_ENDORSEMENT = "Supply meant for export on payment of integrated tax";

/** The counter row uses this instead of a GSTIN when the workspace is not registered. */
export const UNREGISTERED_GSTIN = "UNREGISTERED";

/** Units an invoice line can be measured in. */
export const INVOICE_LINE_UNITS = ["unit", "hour", "day", "month", "word", "post", "piece", "lot", "percent"] as const;
export type InvoiceLineUnit = (typeof INVOICE_LINE_UNITS)[number];
export const invoiceLineUnitSchema = z.enum(INVOICE_LINE_UNITS);

/** Quantities carry 3 decimals (1.5 h = 1500), so hourly billing never rounds early. */
export const QUANTITY_SCALE = 1000;

// ---------------------------------------------------------------------------
// Party snapshots (captured at issue — an edit to the company must never change
// a document that has already been given to the tax authority)
// ---------------------------------------------------------------------------

/** Every key always present, `null` when empty (DTOs are encoded, not parsed). */
export const documentAddressSchema = z.object({
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
});
export type DocumentAddress = z.infer<typeof documentAddressSchema>;

/** Who supplied / who received, frozen at issue (Rule 46 (a)–(f)). */
export const partySnapshotSchema = z.object({
  name: z.string(),
  legalName: z.string().nullable(),
  gstin: z.string().nullable(),
  pan: z.string().nullable(),
  stateCode: z.string().nullable(),
  stateName: z.string().nullable(),
  country: z.string(),
  address: documentAddressSchema,
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** Printed under the signature block on the supplier side. */
  signatoryName: z.string().nullable(),
  signatoryDesignation: z.string().nullable(),
  /** MSME registration, printed when the workspace has one (PRD §5.6). */
  udyamNumber: z.string().nullable(),
});
export type PartySnapshot = z.infer<typeof partySnapshotSchema>;

// ---------------------------------------------------------------------------
// Lines and the SAC-wise summary
// ---------------------------------------------------------------------------

/** One billable line. `taxableMinor` = quantity × unit price − discount. */
export const invoiceLineSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  sortOrder: z.number().int(),
  serviceId: uuidSchema.nullable(),
  description: z.string(),
  sacCode: z.string().nullable(),
  hsnCode: z.string().nullable(),
  /** Thousandths of a unit: 1500 = 1.5. */
  quantityMilli: z.number().int(),
  unit: invoiceLineUnitSchema,
  unitPriceMinor: minorSchema.nullable(),
  discountMinor: minorSchema.nullable(),
  taxableMinor: minorSchema.nullable(),
  taxRateBps: z.number().int(),
  cgstMinor: minorSchema.nullable(),
  sgstMinor: minorSchema.nullable(),
  igstMinor: minorSchema.nullable(),
  cessMinor: minorSchema.nullable(),
  totalMinor: minorSchema.nullable(),
  /** "Social media retainer, October 2026" (PRD US-4.3). */
  periodLabel: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
});
export type InvoiceLineDto = z.infer<typeof invoiceLineSchema>;

/** One row of the SAC-wise summary table printed under the lines (PRD US-4.1). */
export const invoiceTaxSchema = z.object({
  id: uuidSchema,
  sacCode: z.string().nullable(),
  description: z.string().nullable(),
  taxRateBps: z.number().int(),
  taxableMinor: minorSchema.nullable(),
  cgstMinor: minorSchema.nullable(),
  sgstMinor: minorSchema.nullable(),
  igstMinor: minorSchema.nullable(),
  cessMinor: minorSchema.nullable(),
  totalTaxMinor: minorSchema.nullable(),
});
export type InvoiceTaxDto = z.infer<typeof invoiceTaxSchema>;

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

export const invoiceClientRefSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  displayName: z.string(),
});

/** Status transition ledger (US-4.4), append-only in practice. */
export const invoiceStatusHistorySchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  fromStatus: invoiceStatusSchema.nullable(),
  toStatus: invoiceStatusSchema,
  reason: z.string().nullable(),
  actorType: z.string(),
  actorId: uuidSchema.nullable(),
  actorName: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: timestampSchema,
});
export type InvoiceStatusHistoryDto = z.infer<typeof invoiceStatusHistorySchema>;

/** GET /api/v1/invoices/:id (perm `invoices:read`) → the whole document. */
export const invoiceSchema = z.object({
  id: uuidSchema,
  docType: invoiceDocTypeSchema,
  docTypeLabel: z.string(),
  /** NULL while draft — drafts never consume a number (Rule 46(b), principles §7.4). */
  number: z.string().nullable(),
  seriesPrefix: z.string(),
  /** "2026-27", from the document date in the org's timezone. */
  financialYear: z.string().nullable(),
  sequence: z.number().int().nullable(),
  status: invoiceStatusSchema,
  statusLabel: z.string(),
  client: invoiceClientRefSchema,
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  sourceInvoiceId: uuidSchema.nullable(),
  sourceInvoiceNumber: z.string().nullable(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  paymentTermsDays: z.number().int(),
  supplierGstin: z.string().nullable(),
  supplierStateCode: z.string().nullable(),
  placeOfSupplyStateCode: z.string().nullable(),
  placeOfSupplyStateName: z.string().nullable(),
  placeOfSupplyCountry: z.string(),
  supplyType: gstSupplyTypeSchema,
  reverseCharge: z.boolean(),
  exportWithTax: z.boolean(),
  lutNumber: z.string().nullable(),
  /** Rule 46(o) endorsement for exports, else null. */
  exportEndorsement: z.string().nullable(),
  currency: currencyCodeSchema,
  /** Rate to the org base currency, snapshotted at issue (item 17). */
  exchangeRateToBase: z.string(),
  subtotalMinor: minorSchema.nullable(),
  discountMinor: minorSchema.nullable(),
  taxableMinor: minorSchema.nullable(),
  cgstMinor: minorSchema.nullable(),
  sgstMinor: minorSchema.nullable(),
  igstMinor: minorSchema.nullable(),
  cessMinor: minorSchema.nullable(),
  roundOffMinor: minorSchema.nullable(),
  totalMinor: minorSchema.nullable(),
  paidMinor: minorSchema.nullable(),
  tdsMinor: minorSchema.nullable(),
  creditedMinor: minorSchema.nullable(),
  writtenOffMinor: minorSchema.nullable(),
  balanceMinor: minorSchema.nullable(),
  /** Indian-system words for the total, e.g. "Rupees One Lakh Only" (INR only). */
  amountInWords: z.string().nullable(),
  poNumber: z.string().nullable(),
  notes: z.string().nullable(),
  terms: z.string().nullable(),
  buyerSnapshot: partySnapshotSchema.nullable(),
  supplierSnapshot: partySnapshotSchema.nullable(),
  pdfFileId: uuidSchema.nullable(),
  issuedAt: timestampSchema.nullable(),
  sentAt: timestampSchema.nullable(),
  viewedAt: timestampSchema.nullable(),
  cancelledAt: timestampSchema.nullable(),
  cancelReason: z.string().nullable(),
  /** RV only: how much of the advance has been adjusted into a tax invoice. */
  adjustedMinor: minorSchema.nullable(),
  /** Of a receipt voucher, how much has been GIVEN BACK (Rule 51) rather than adjusted. */
  refundedMinor: minorSchema.nullable(),
  adjustedInvoiceId: uuidSchema.nullable(),
  lines: z.array(invoiceLineSchema),
  taxes: z.array(invoiceTaxSchema),
  customFields: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  ...timestampsShape,
});
export type InvoiceDto = z.infer<typeof invoiceSchema>;

/** The row shape lists, the client 360, the dashboard and the portal all read. */
export const invoiceSummarySchema = z.object({
  id: uuidSchema,
  docType: invoiceDocTypeSchema,
  number: z.string().nullable(),
  status: invoiceStatusSchema,
  statusLabel: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  projectId: uuidSchema.nullable(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  currency: currencyCodeSchema,
  totalMinor: minorSchema.nullable(),
  paidMinor: minorSchema.nullable(),
  balanceMinor: minorSchema.nullable(),
  /** Whole days past `dueDate` while a balance remains; 0 otherwise. */
  daysOverdue: z.number().int(),
  isOverdue: z.boolean(),
  ...timestampsShape,
});
export type InvoiceSummaryDto = z.infer<typeof invoiceSummarySchema>;

/** One row per currency, because we never convert without an explicit FX rate. */
export const invoiceTotalsSchema = z.object({
  currency: currencyCodeSchema,
  count: z.number().int(),
  totalMinor: minorSchema.nullable(),
  paidMinor: minorSchema.nullable(),
  balanceMinor: minorSchema.nullable(),
  overdueMinor: minorSchema.nullable(),
});
export type InvoiceTotalsDto = z.infer<typeof invoiceTotalsSchema>;

/** GET /api/v1/invoices (perm `invoices:read`) → a page plus per-currency totals. */
export const invoicePageSchema = z.object({
  items: z.array(invoiceSummarySchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  totals: z.array(invoiceTotalsSchema),
});
export type InvoicePage = z.infer<typeof invoicePageSchema>;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const sacCodeSchema = z.string().trim().regex(/^\d{4,8}$/, "SAC/HSN must be 4–8 digits");

/** A line as the client sends it. Tax is ALWAYS computed server-side. */
export const invoiceLineInputSchema = z
  .object({
    serviceId: uuidSchema.nullable().optional(),
    description: z.string().trim().min(1).max(500),
    sacCode: blankToNull(sacCodeSchema).optional(),
    hsnCode: blankToNull(sacCodeSchema).optional(),
    quantityMilli: z.number().int().min(1).max(1_000_000_000).optional(),
    unit: invoiceLineUnitSchema.optional(),
    unitPriceMinor: minorSchema.min(0),
    discountMinor: minorSchema.min(0).optional(),
    taxRateBps: z.number().int().min(0).max(10_000).optional(),
    periodLabel: blankToNull(z.string().max(120)).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type InvoiceLineInput = z.input<typeof invoiceLineInputSchema>;
export type InvoiceLineData = z.output<typeof invoiceLineInputSchema>;

/** Writable document fields WITHOUT defaults, so `.partial()` cannot resurrect them. */
const invoiceWritableShape = {
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  paymentTermsDays: z.number().int().min(0).max(365),
  supplierGstin: blankToNull(z.string().trim().toUpperCase().max(15)),
  placeOfSupplyStateCode: gstStateCodeSchema.nullable(),
  placeOfSupplyCountry: z.string().trim().toUpperCase().length(2),
  reverseCharge: z.boolean(),
  exportWithTax: z.boolean(),
  lutNumber: blankToNull(z.string().max(50)),
  currency: currencyCodeSchema,
  exchangeRateToBase: z.string().regex(/^\d+(\.\d{1,10})?$/, "Use a decimal rate like 83.25"),
  roundOff: z.boolean(),
  poNumber: blankToNull(z.string().max(60)),
  notes: blankToNull(z.string().max(2000)),
  terms: blankToNull(z.string().max(5000)),
  customFields: z.record(z.string(), z.unknown()),
  lines: z.array(invoiceLineInputSchema).min(1).max(200),
};

/** POST /api/v1/invoices (perm `invoices:write`) → 201 Invoice (always a draft). */
export const createInvoiceInputSchema = z
  .object({
    clientId: uuidSchema,
    docType: invoiceDocTypeSchema.optional(),
    sourceInvoiceId: uuidSchema.nullable().optional(),
    ...invoiceWritableShape,
    projectId: invoiceWritableShape.projectId.optional(),
    engagementId: invoiceWritableShape.engagementId.optional(),
    invoiceDate: invoiceWritableShape.invoiceDate.optional(),
    dueDate: invoiceWritableShape.dueDate.optional(),
    paymentTermsDays: invoiceWritableShape.paymentTermsDays.optional(),
    supplierGstin: invoiceWritableShape.supplierGstin.optional(),
    placeOfSupplyStateCode: invoiceWritableShape.placeOfSupplyStateCode.optional(),
    placeOfSupplyCountry: invoiceWritableShape.placeOfSupplyCountry.optional(),
    reverseCharge: invoiceWritableShape.reverseCharge.optional(),
    exportWithTax: invoiceWritableShape.exportWithTax.optional(),
    lutNumber: invoiceWritableShape.lutNumber.optional(),
    currency: invoiceWritableShape.currency.optional(),
    exchangeRateToBase: invoiceWritableShape.exchangeRateToBase.optional(),
    roundOff: invoiceWritableShape.roundOff.optional(),
    poNumber: invoiceWritableShape.poNumber.optional(),
    notes: invoiceWritableShape.notes.optional(),
    terms: invoiceWritableShape.terms.optional(),
    customFields: invoiceWritableShape.customFields.optional(),
  })
  .strict();
export type CreateInvoiceInput = z.input<typeof createInvoiceInputSchema>;
export type CreateInvoiceData = z.output<typeof createInvoiceInputSchema>;

/**
 * PATCH /api/v1/invoices/:id (perm `invoices:write`) → Invoice.
 * Merge patch; `lines` are replaced AS A WHOLE. Only a draft can be patched —
 * an issued document answers 409 (corrections are credit notes, PRD US-4.1).
 * A stale `version` answers 409 with the current one.
 */
export const updateInvoiceInputSchema = z
  .object({ ...invoiceWritableShape, version: z.number().int().min(1) })
  .partial()
  .strict();
export type UpdateInvoiceInput = z.input<typeof updateInvoiceInputSchema>;
export type UpdateInvoiceData = z.output<typeof updateInvoiceInputSchema>;

/**
 * POST /api/v1/invoices/:id/issue (perm `invoices:send`, **Idempotency-Key required**)
 * → Invoice. Allocates the number, snapshots both parties and locks the document,
 * all in one SERIALIZABLE transaction. Re-issuing returns the existing document.
 * Missing Rule 46 fields answer 422 with `details.missing[]`.
 */
export const issueInvoiceInputSchema = z.object({ invoiceDate: dateOnlySchema.optional() }).strict();
export type IssueInvoiceInput = z.input<typeof issueInvoiceInputSchema>;

/** POST /api/v1/invoices/:id/cancel (perm `invoices:send`) → Invoice. Unpaid documents only. */
export const cancelInvoiceInputSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
export type CancelInvoiceInput = z.input<typeof cancelInvoiceInputSchema>;
export type CancelInvoiceData = z.output<typeof cancelInvoiceInputSchema>;

/** POST /api/v1/invoices/:id/credit-notes (perm `invoices:write`) → 201 draft credit note. */
export const createCreditNoteInputSchema = z
  .object({
    reason: z.string().trim().min(3).max(500),
    invoiceDate: dateOnlySchema.optional(),
    /** Omit for a FULL credit note (every line copied); send lines for a partial one. */
    lines: z.array(invoiceLineInputSchema).min(1).max(200).optional(),
    notes: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type CreateCreditNoteInput = z.input<typeof createCreditNoteInputSchema>;
export type CreateCreditNoteData = z.output<typeof createCreditNoteInputSchema>;

/**
 * POST /api/v1/invoices/:id/debit-notes (perm `invoices:write`) → 201 draft debit note.
 *
 * Section 34(3): raised when the taxable value or the tax on an already-issued
 * invoice turns out to be LESS than it should have been. It increases what the
 * client owes, so unlike a credit note it is a receivable in its own right —
 * it has its own number series, its own due date and its own balance, and it
 * is NOT capped by the source invoice's remaining balance. Undercharging by
 * more than is currently outstanding is exactly the case this exists for.
 */
export const createDebitNoteInputSchema = z
  .object({
    reason: z.string().trim().min(3).max(500),
    invoiceDate: dateOnlySchema.optional(),
    /** What was undercharged. Required: there is no sensible "copy every line" default. */
    lines: z.array(invoiceLineInputSchema).min(1).max(200),
    dueDate: dateOnlySchema.nullable().optional(),
    notes: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type CreateDebitNoteInput = z.input<typeof createDebitNoteInputSchema>;
export type CreateDebitNoteData = z.output<typeof createDebitNoteInputSchema>;

/**
 * POST /api/v1/invoices/:id/refund-vouchers (perm `invoices:write`) → 201 draft refund voucher.
 *
 * Rule 51: issued when an advance was taken against a supply that is not going
 * to happen, and the money goes back. It refunds the part of a receipt voucher
 * that has NOT been adjusted into an invoice — the adjusted part is already
 * settled against real supplies and is not ours to give back.
 *
 * This is what makes a partly-used advance reversible. Until now an advance
 * could only be voided while none of it had been used, so a client who paid
 * ₹1,00,000 up front, consumed ₹40,000 of it and then cancelled had no way out
 * of the system.
 */
export const createRefundVoucherInputSchema = z
  .object({
    reason: z.string().trim().min(3).max(500),
    /** Minor units. Omit to refund everything still unadjusted. */
    amountMinor: minorSchema.min(1).optional(),
    invoiceDate: dateOnlySchema.optional(),
    notes: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type CreateRefundVoucherInput = z.input<typeof createRefundVoucherInputSchema>;
export type CreateRefundVoucherData = z.output<typeof createRefundVoucherInputSchema>;

/** POST /api/v1/receipt-vouchers (perm `invoices:write`) → 201 draft receipt voucher. */
export const createReceiptVoucherInputSchema = z
  .object({
    clientId: uuidSchema,
    /** Money actually received. Tax-inclusive by default, as an advance normally is. */
    amountMinor: minorSchema.min(1),
    taxInclusive: z.boolean().optional(),
    receivedOn: dateOnlySchema.optional(),
    taxRateBps: z.number().int().min(0).max(10_000).optional(),
    sacCode: blankToNull(sacCodeSchema).optional(),
    description: z.string().trim().min(1).max(500).optional(),
    currency: currencyCodeSchema.optional(),
    engagementId: uuidSchema.nullable().optional(),
    notes: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type CreateReceiptVoucherInput = z.input<typeof createReceiptVoucherInputSchema>;
export type CreateReceiptVoucherData = z.output<typeof createReceiptVoucherInputSchema>;

/**
 * POST /api/v1/invoices/:id/bill-time (perms `invoices:write` + `financials:view`).
 *
 * Bill time onto a DRAFT in ONE transaction: the draft is locked, the chosen
 * hours and expenses are stamped onto it (exactly once, or 409), and the lines
 * are built from the rows read under that lock and written in the same commit.
 * It replaced a three-request browser saga (add lines, stamp, take lines back
 * out on error) that could leave lines without stamps or stamps without lines.
 */
export const billTimeOntoInvoiceInputSchema = z
  .object({
    /** The draft version the person was looking at; a stale one is a 409. */
    version: z.number().int().optional(),
    entryIds: z.array(uuidSchema).max(2000).optional(),
    expenseIds: z.array(uuidSchema).max(2000).optional(),
    groupBy: z.enum(UNBILLED_GROUPINGS).default("task"),
    /** Defaults to the workspace's default SAC. */
    sacCode: blankToNull(sacCodeSchema).optional(),
    /** Defaults to the workspace's default rate; a bill of supply is always 0%. */
    taxRateBps: z.number().int().min(0).max(10_000).optional(),
  })
  .strict();
export type BillTimeOntoInvoiceInput = z.input<typeof billTimeOntoInvoiceInputSchema>;
export type BillTimeOntoInvoiceData = z.output<typeof billTimeOntoInvoiceInputSchema>;

export const billTimeOntoInvoiceResultSchema = z.object({
  invoice: invoiceSchema,
  timeEntries: z.number().int(),
  expenses: z.number().int(),
  minutes: z.number().int(),
  /** The gross of the lines added — equal, to the paisa, to the groups the person chose. */
  amountMinor: minorSchema,
  currency: currencyCodeSchema,
});
export type BillTimeOntoInvoiceResultDto = z.infer<typeof billTimeOntoInvoiceResultSchema>;

/** POST /api/v1/invoices/from-engagement (perm `invoices:write`) → 201 draft retainer invoice. */
export const invoiceFromEngagementInputSchema = z
  .object({
    engagementId: uuidSchema,
    /** The period the lines are labelled with, e.g. 2026-10-01 → 2026-10-31. */
    periodStart: dateOnlySchema,
    periodEnd: dateOnlySchema,
    invoiceDate: dateOnlySchema.optional(),
    dueDate: dateOnlySchema.nullable().optional(),
    sacCode: blankToNull(sacCodeSchema).optional(),
    taxRateBps: z.number().int().min(0).max(10_000).optional(),
    notes: blankToNull(z.string().max(2000)).optional(),
  })
  .strict();
export type InvoiceFromEngagementInput = z.input<typeof invoiceFromEngagementInputSchema>;
export type InvoiceFromEngagementData = z.output<typeof invoiceFromEngagementInputSchema>;

/** POST /api/v1/proforma/:id/convert-to-invoice (perm `invoices:write`) → 201 draft tax invoice. */
export const convertProformaInputSchema = z.object({ invoiceDate: dateOnlySchema.optional() }).strict();
export type ConvertProformaInput = z.input<typeof convertProformaInputSchema>;

/** POST /api/v1/invoices/preview-tax (perm `invoices:write`) → live tax while editing. */
export const previewTaxInputSchema = z
  .object({
    clientId: uuidSchema,
    docType: invoiceDocTypeSchema.optional(),
    supplierGstin: blankToNull(z.string().trim().toUpperCase().max(15)).optional(),
    invoiceDate: dateOnlySchema.optional(),
    currency: currencyCodeSchema.optional(),
    placeOfSupplyStateCode: gstStateCodeSchema.nullable().optional(),
    reverseCharge: z.boolean().optional(),
    exportWithTax: z.boolean().optional(),
    roundOff: z.boolean().optional(),
    lines: z.array(invoiceLineInputSchema).min(1).max(200),
  })
  .strict();
export type PreviewTaxInput = z.input<typeof previewTaxInputSchema>;
export type PreviewTaxData = z.output<typeof previewTaxInputSchema>;

/** What the editor shows while you type, and what `/invoices/:id/preview-tax` returns. */
export const taxPreviewSchema = z.object({
  currency: currencyCodeSchema,
  supplyType: gstSupplyTypeSchema,
  supplierStateCode: z.string().nullable(),
  placeOfSupplyStateCode: z.string().nullable(),
  placeOfSupplyStateName: z.string().nullable(),
  placeOfSupplyCountry: z.string(),
  reverseCharge: z.boolean(),
  exportEndorsement: z.string().nullable(),
  subtotalMinor: minorSchema,
  discountMinor: minorSchema,
  taxableMinor: minorSchema,
  cgstMinor: minorSchema,
  sgstMinor: minorSchema,
  igstMinor: minorSchema,
  cessMinor: minorSchema,
  roundOffMinor: minorSchema,
  totalMinor: minorSchema,
  amountInWords: z.string().nullable(),
  lines: z.array(
    z.object({
      sortOrder: z.number().int(),
      description: z.string(),
      sacCode: z.string().nullable(),
      quantityMilli: z.number().int(),
      unitPriceMinor: minorSchema,
      discountMinor: minorSchema,
      taxableMinor: minorSchema,
      taxRateBps: z.number().int(),
      cgstMinor: minorSchema,
      sgstMinor: minorSchema,
      igstMinor: minorSchema,
      cessMinor: minorSchema,
      totalMinor: minorSchema,
    }),
  ),
  taxes: z.array(
    z.object({
      sacCode: z.string().nullable(),
      taxRateBps: z.number().int(),
      taxableMinor: minorSchema,
      cgstMinor: minorSchema,
      sgstMinor: minorSchema,
      igstMinor: minorSchema,
      cessMinor: minorSchema,
      totalTaxMinor: minorSchema,
    }),
  ),
});
export type TaxPreviewDto = z.infer<typeof taxPreviewSchema>;

// ---------------------------------------------------------------------------
// Numbering preview and books lock
// ---------------------------------------------------------------------------

/** GET /api/v1/invoices/next-number (perm `invoices:read`) — a PREVIEW, never consumed. */
export const nextNumberQuerySchema = z.object({
  docType: invoiceDocTypeSchema.optional(),
  date: dateOnlySchema.optional(),
  supplierGstin: z.string().trim().toUpperCase().max(15).optional(),
});
export type NextNumberQuery = z.infer<typeof nextNumberQuerySchema>;

export const nextNumberPreviewSchema = z.object({
  docType: invoiceDocTypeSchema,
  prefix: z.string(),
  financialYear: z.string(),
  supplierGstin: z.string(),
  nextSequence: z.number().int(),
  number: z.string(),
});
export type NextNumberPreviewDto = z.infer<typeof nextNumberPreviewSchema>;

/**
 * GET/PATCH /api/v1/invoices/settings (read `invoices:read`, write `org:manage`).
 * `booksLockedUpTo` freezes every document dated on or before it (PRD US-4.1).
 */
export const invoiceModuleSettingsSchema = z.object({
  booksLockedUpTo: dateOnlySchema.nullable(),
  signatoryName: z.string().nullable(),
  signatoryDesignation: z.string().nullable(),
  signatureFileId: uuidSchema.nullable(),
  udyamNumber: z.string().nullable(),
  defaultRoundOff: z.boolean(),
  updatedAt: timestampSchema,
});
export type InvoiceModuleSettingsDto = z.infer<typeof invoiceModuleSettingsSchema>;

export const updateInvoiceSettingsInputSchema = z
  .object({
    booksLockedUpTo: dateOnlySchema.nullable(),
    signatoryName: blankToNull(z.string().max(120)),
    signatoryDesignation: blankToNull(z.string().max(120)),
    signatureFileId: uuidSchema.nullable(),
    udyamNumber: blankToNull(z.string().max(30)),
    defaultRoundOff: z.boolean(),
  })
  .partial()
  .strict();
export type UpdateInvoiceSettingsInput = z.input<typeof updateInvoiceSettingsInputSchema>;
export type UpdateInvoiceSettingsData = z.output<typeof updateInvoiceSettingsInputSchema>;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /api/v1/invoices (perm `invoices:read`). */
export const listInvoicesQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  docType: csvArraySchema(invoiceDocTypeSchema, INVOICE_DOC_TYPES.length).optional(),
  status: csvArraySchema(invoiceStatusSchema, INVOICE_STATUSES.length).optional(),
  /** "2026-27" — the financial year the document date falls in. */
  fy: z.string().regex(/^\d{4}-\d{2}$/, "Use a financial year like 2026-27").optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  overdue: queryBooleanSchema.optional(),
  sort: z.enum(["invoiceDate", "dueDate", "number", "totalMinor", "balanceMinor", "createdAt"]).optional(),
});
export type ListInvoicesQuery = z.infer<typeof listInvoicesQuerySchema>;

export const invoiceIdParamSchema = idParamSchema;

// ---------------------------------------------------------------------------
// Pure helpers shared by the API and the web app
// ---------------------------------------------------------------------------

/** Taxable value of a line: quantity × unit price − discount, in minor units. */
export function lineTaxableMinor(quantityMilli: number, unitPriceMinor: number, discountMinor: number): number {
  const gross = Math.round((quantityMilli * unitPriceMinor) / QUANTITY_SCALE);
  return Math.max(0, gross - discountMinor);
}

/** Gross value of a line before its discount. */
export function lineGrossMinor(quantityMilli: number, unitPriceMinor: number): number {
  return Math.round((quantityMilli * unitPriceMinor) / QUANTITY_SCALE);
}

/**
 * Round-off to the nearest rupee, as its own line (PRD US-4.1). Returns the
 * adjustment to ADD to the pre-round total; only whole-rupee currencies with
 * paise (INR) are rounded.
 */
export function roundOffMinor(totalMinor: number, currency: string): number {
  if (currency !== "INR") return 0;
  const remainder = totalMinor % 100;
  if (remainder === 0) return 0;
  return remainder >= 50 ? 100 - remainder : -remainder;
}

/** Due date from the invoice date and the client's payment terms. */
export function dueDateFrom(invoiceDate: string, paymentTermsDays: number): string {
  const d = new Date(`${invoiceDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + paymentTermsDays);
  return d.toISOString().slice(0, 10);
}

/** Whole days a document is past due; 0 when not due yet or fully settled. */
export function daysOverdue(dueDate: string | null, balanceMinor: number, today: string): number {
  if (!dueDate || balanceMinor <= 0) return 0;
  const due = Date.parse(`${dueDate}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(due) || !Number.isFinite(now) || now <= due) return 0;
  return Math.floor((now - due) / 86_400_000);
}

/** True when the rendered number satisfies Rule 46(b) (≤ 16 chars, A–Z 0–9 - /). */
export function isValidDocumentNumber(value: string): boolean {
  return GST_INVOICE_NUMBER_REGEX.test(value);
}

/** The endorsement an export document must print, or null for a domestic one. */
export function exportEndorsementFor(supplyType: string, exportWithTax: boolean): string | null {
  if (supplyType !== "export") return null;
  return exportWithTax ? EXPORT_WITH_TAX_ENDORSEMENT : LUT_ENDORSEMENT;
}
